import React from 'react'
import { CONTACT_CONFIG, CONTACT_DETAILS } from '../../config/constants.js'
import { useLandingContent } from '../../hooks/i18n/usePageContent.js'
import * as TOKENS from '../chat/design-tokens'

interface ContactSectionProps {
  contactRef: React.RefObject<HTMLElement>
  formState: {
    name: string
    company: string
    email: string
    interest: string
    message: string
  }
  handleFormChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => void
  handleFormSubmit: (e: React.FormEvent) => void
  isSubmitting?: boolean
  submitStatus?: 'idle' | 'success' | 'error'
  fieldErrors?: Record<string, string>
}

const sectionShell = `w-full scroll-mt-24 px-6 py-20 md:px-12 border-t ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}`
const eyebrow = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`

const fieldBase = `
  w-full px-4 py-3 rounded-none
  text-sm text-foreground placeholder:text-muted-foreground
  bg-background border ${TOKENS.BORDER.default}
  transition-colors duration-150
  hover:border-foreground/30
  focus:outline-none focus:border-foreground
  focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
  appearance-none
`

const fieldError = 'border-red-500/60 focus:border-red-500'

function localizedContactLabel(
  englishLabel: string,
  labels: { email: string; phone: string; location: string }
): string {
  switch (englishLabel) {
    case 'Email':
      return labels.email
    case 'Phone':
      return labels.phone
    case 'Location':
      return labels.location
    default:
      return englishLabel
  }
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  contactRef,
  formState,
  handleFormChange,
  handleFormSubmit,
  isSubmitting = false,
  submitStatus = 'idle',
  fieldErrors = {},
}) => {
  const { contact } = useLandingContent()
  const { form } = contact

  return (
    <section
      ref={contactRef}
      id="contact"
      aria-labelledby="contact-heading"
      className={`${sectionShell} relative overflow-hidden`}
    >
      <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-16 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-10 lg:max-w-sm">
          <div className="flex flex-col gap-4">
            <span className={eyebrow}>{contact.eyebrow}</span>
            <h2
              id="contact-heading"
              className={`text-3xl font-bold tracking-tight md:text-4xl ${TOKENS.TEXT.primary}`}
            >
              {contact.title}
            </h2>
            <p className={`text-lg font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
              {contact.body}
            </p>
          </div>

          <ul className="flex flex-col gap-4" aria-label={form.ariaLabel}>
            {CONTACT_DETAILS.map(item => (
              <li key={item.label} className="flex flex-col gap-1">
                <span className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}>
                  {localizedContactLabel(item.label, contact.contactDetailLabels)}
                </span>
                {item.href ? (
                  <a
                    href={item.href}
                    className={`
                      inline-flex items-center gap-2 text-sm font-medium
                      ${TOKENS.TEXT.primary}
                      transition-colors duration-150 ${TOKENS.BRAND.accentHover}
                      ${TOKENS.INTERACTION.focusRing}
                    `}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.label === 'Email' ? TOKENS.BRAND.accentBg : 'bg-muted-foreground'}`}
                      aria-hidden="true"
                    />
                    {item.value}
                  </a>
                ) : (
                  <span className={`inline-flex items-center gap-2 text-sm font-medium ${TOKENS.TEXT.primary}`}>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" aria-hidden="true" />
                    {contact.location}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <a
            href={CONTACT_CONFIG.SCHEDULING.BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`
              group relative inline-flex items-center gap-2 self-start
              overflow-hidden rounded-none border px-5 py-2.5
              text-sm font-medium
              ${TOKENS.BORDER.default} bg-background ${TOKENS.TEXT.primary}
              transition-colors duration-150 hover:bg-muted
              ${TOKENS.INTERACTION.focusRing}
            `}
          >
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{contact.bookCall}</span>
            <svg
              className="relative h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
            >
              <path d="M7 17L17 7M7 7h10v10" />
            </svg>
          </a>
        </div>

        <div className={`flex-1 border p-8 md:p-10 ${TOKENS.BORDER.default} bg-background`}>
          <form
            onSubmit={handleFormSubmit}
            noValidate
            aria-label={form.ariaLabel}
            className="flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-name"
                  className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}
                >
                  {form.labels.name}{' '}
                  <span aria-hidden="true">{contact.form.requiredIndicator}</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleFormChange}
                  placeholder={form.placeholders.name}
                  required
                  autoComplete="name"
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                  className={`${fieldBase} ${fieldErrors.name ? fieldError : ''}`}
                />
                {fieldErrors.name && (
                  <p id="contact-name-error" role="alert" className={`${TOKENS.TEXT_SIZE.xs} ${TOKENS.STATE_COLOR.error}`}>
                    {fieldErrors.name}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-company"
                  className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}
                >
                  {form.labels.company}
                </label>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  value={formState.company}
                  onChange={handleFormChange}
                  placeholder={form.placeholders.company}
                  autoComplete="organization"
                  className={fieldBase}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="contact-email"
                className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}
              >
                {form.labels.email}{' '}
                <span aria-hidden="true">{contact.form.requiredIndicator}</span>
              </label>
              <input
                id="contact-email"
                type="email"
                name="email"
                value={formState.email}
                onChange={handleFormChange}
                placeholder={form.placeholders.email}
                required
                autoComplete="email"
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                className={`${fieldBase} ${fieldErrors.email ? fieldError : ''}`}
              />
              {fieldErrors.email && (
                <p id="contact-email-error" role="alert" className={`${TOKENS.TEXT_SIZE.xs} ${TOKENS.STATE_COLOR.error}`}>
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="contact-interest"
                className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}
              >
                {form.labels.interest}
              </label>
              <div className="relative">
                <select
                  id="contact-interest"
                  name="interest"
                  value={formState.interest}
                  onChange={handleFormChange}
                  className={fieldBase}
                >
                  <option value="" disabled>{form.placeholders.interest}</option>
                  {contact.interests.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <svg
                  className={`pointer-events-none absolute right-4 top-1/2 h-3 w-3 -translate-y-1/2 ${TOKENS.TEXT.muted}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="contact-message"
                className={`${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest ${TOKENS.TEXT.muted}`}
              >
                {form.labels.message}
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={formState.message}
                onChange={handleFormChange}
                placeholder={form.placeholders.message}
                rows={4}
                className={`${fieldBase} resize-none`}
              />
            </div>

            {submitStatus === 'success' && (
              <div
                role="status"
                className={`
                  flex items-center gap-2 border px-4 py-3 text-sm
                  border-green-500 bg-green-500/10 ${TOKENS.SEMANTIC.greenText}
                `}
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                {contact.success}
              </div>
            )}

            {submitStatus === 'error' && (
              <div
                role="alert"
                className={`
                  flex items-center gap-2 border px-4 py-3 text-sm font-medium
                  border-red-500 bg-red-500/10 ${TOKENS.SEMANTIC.redText}
                `}
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                {contact.error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`
                group relative flex w-full items-center justify-center gap-2
                overflow-hidden rounded-none border border-transparent px-8 py-4
                ${TOKENS.TEXT_SIZE.sm} font-medium tracking-wide
                bg-foreground text-background
                transition-colors duration-150 hover:bg-foreground/90
                ${TOKENS.INTERACTION.focusRing}
                disabled:cursor-not-allowed disabled:opacity-50
              `}
            >
              <span className={TOKENS.INTERACTION.shimmer} />
              {isSubmitting && (
                <svg className="relative h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              <span className="relative">
                {isSubmitting ? contact.submit.sending : contact.submit.idle}
              </span>
              {!isSubmitting && (
                <svg
                  className="relative h-4 w-4 transition-transform duration-150 group-hover:translate-x-1"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>

            <p className={`${TOKENS.TEXT_SIZE.tiny} leading-relaxed ${TOKENS.TEXT.muted}`}>
              {contact.legalNote.prefix}{' '}
              <a
                href="/privacy"
                className={`underline decoration-border transition-colors duration-150 hover:text-foreground ${TOKENS.INTERACTION.focusRing}`}
              >
                {contact.legalNote.privacyPolicy}
              </a>
              {contact.legalNote.suffix}
            </p>
          </form>
        </div>
      </div>
    </section>
  )
}
